import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Loader2, CheckCircle } from "lucide-react";
import { useStore } from "@/store/useStore";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { useEffect } from 'react';

const formSchema = z.object({
  fullName: z.string().min(2, "Nome e cognome richiesti"),
  email: z.string().email("Email non valida"),
  phone: z.string().min(6, "Numero di telefono non valido"),
  address: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface PriceLockRequestModalProps {
  dealerId: string;
  dealerName: string;
  metal: "ORO" | "ARGENTO" | "gold" | "silver";
  purity?: string;
  weight?: number;
  estimatedValue?: number;
  pricePerGram?: number;
  // Support both naming conventions
  isOpen?: boolean;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PriceLockRequestModal({
  dealerId,
  dealerName,
  metal,
  purity = "24KT",
  weight = 1,
  estimatedValue = 0,
  pricePerGram = 0,
  isOpen,
  onClose,
  open,
  onOpenChange
}: PriceLockRequestModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const { user, isAuthenticated } = useStore();
  const { isRecaptchaLoaded, executeRecaptcha, recaptchaError } = useRecaptcha();
  
  const metalDisplay = metal === 'gold' || metal === 'ORO' ? 'ORO' : 'ARGENTO';
  
  const dialogOpen = isOpen !== undefined ? isOpen : open;
  
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (onClose) {
        onClose();
      } else if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };
  
  useEffect(() => {
    if (recaptchaError) {
      console.error("reCAPTCHA error:", recaptchaError);
      toast({
        title: "Errore di sicurezza",
        description: "Impossibile caricare il sistema di sicurezza. Ricarica la pagina.",
        variant: "destructive",
      });
    }
  }, [recaptchaError, toast]);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: user?.name || "",
      email: user?.email || "",
      phone: "",
      address: "",
    },
  });
  
  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      
      if (!isRecaptchaLoaded) {
        throw new Error("Sistema di sicurezza non disponibile. Ricarica la pagina.");
      }
      
      const recaptchaToken = await executeRecaptcha('price_lock');
      console.log("reCAPTCHA token:", recaptchaToken);
      
      const lockData = {
        dealer_id: dealerId,
        user_id: isAuthenticated ? user?.id : null,
        metal: metalDisplay,
        purity: purity,
        price: pricePerGram,
        weight: weight,
        status: "ACTIVE",
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        contact_info: {
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          address: data.address || "",
        },
        recaptcha_token: recaptchaToken,
      };
      
      const { error } = await supabase
        .from('price_checkpoints')
        .insert([lockData]);
      
      if (error) {
        throw error;
      }
      
      if (!isAuthenticated) {
        const savedRequests = JSON.parse(localStorage.getItem('priceLockRequests') || '[]');
        savedRequests.push({
          ...lockData,
          created_at: new Date().toISOString(),
        });
        localStorage.setItem('priceLockRequests', JSON.stringify(savedRequests));
      }
      
      setIsSuccess(true);
      
      toast({
        title: "Prezzo bloccato con successo!",
        description: `Hai bloccato il prezzo per 24 ore. Scadenza: ${new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleString()}`,
      });
      
      setTimeout(() => {
        setIsSuccess(false);
        if (onClose) {
          onClose();
        } else if (onOpenChange) {
          onOpenChange(false);
        }
        form.reset();
      }, 3000);
      
    } catch (error) {
      console.error("Errore durante il blocco del prezzo:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore durante il blocco del prezzo. Riprova più tardi.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  function formatPrice(price: number) {
    return price.toLocaleString('it-IT', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2
    });
  }
  
  const submitDisabled = isSubmitting || !isRecaptchaLoaded;
  
  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md card-light-bg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold card-text">
            Blocca Prezzo per 24h
          </DialogTitle>
        </DialogHeader>
        
        {isSuccess ? (
          <div className="py-6 text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h3 className="text-lg font-medium card-text">Prezzo Bloccato!</h3>
            <p className="text-muted-foreground">
              Il prezzo è stato bloccato con successo per 24 ore presso {dealerName}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-muted p-3 rounded-md mb-4">
              <h4 className="font-medium mb-2 card-text">Riepilogo</h4>
              <div className="space-y-1 text-sm">
                <div className="summary-item">
                  <span className="summary-label">Compro Oro:</span>
                  <span className="summary-value">{dealerName}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Metallo:</span>
                  <span className="summary-value">{metalDisplay}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Purezza:</span>
                  <span className="summary-value">{purity}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Peso:</span>
                  <span className="summary-value">{weight}g</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Prezzo/g:</span>
                  <span className="summary-value">{formatPrice(pricePerGram)}</span>
                </div>
                <div className="summary-item font-medium pt-1 border-t">
                  <span className="card-text">Valore stimato:</span>
                  <span className="metal-text">{formatPrice(estimatedValue)}</span>
                </div>
              </div>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="card-label">Nome e Cognome</FormLabel>
                      <FormControl>
                        <Input {...field} className="card-text" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="card-label">Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} className="card-text" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="card-label">Telefono</FormLabel>
                      <FormControl>
                        <Input type="tel" {...field} className="card-text" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="card-label">Indirizzo (opzionale)</FormLabel>
                      <FormControl>
                        <Input {...field} className="card-text" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {!isAuthenticated && (
                  <div className="rounded-md bg-yellow-50 p-3 text-yellow-800 text-sm">
                    <p className="font-medium">Non hai effettuato l'accesso</p>
                    <p className="mt-1">
                      Registrati o accedi per gestire facilmente le tue richieste di blocco prezzo.
                    </p>
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground text-center mb-4">
                  Questo sito è protetto da reCAPTCHA e si applicano la <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> e i <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Termini di Servizio</a> di Google.
                </div>
                
                <div className="flex justify-end">
                  <Button type="submit" className="metal-accent" disabled={submitDisabled}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Elaborazione...
                      </>
                    ) : (
                      "Conferma Blocco Prezzo"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
